import { Injectable } from '@angular/core';
import { UrlService } from '../shared/services/url/url.service';
import { CalculatorService } from '../shared/services/calculator/calculator.service';
import { DocumentDataBuilder } from '../document-data/document-data.builder';
import { Invoice } from './invoice.model';
import {
  IFetchFilterKey,
  ISearchParams,
  SEARCH_LIST_FILTER,
  SearchParamsEnum,
  SortByEnum,
  SortOrderEnum,
} from '../entity-data/entity-data.data';
import { EntityTypesEnum } from '../../app.enum';
import {
  ALL_YEARS,
  DocumentOverallStatesEnum,
  DocumentTypesEnum,
} from '../document-data/document-data.data';
import {
  InvoiceFiltersEnum,
  PAID_LIST_SEARCH_PARAMS,
  UNPAID_LIST_SEARCH_PARAMS,
} from './invoice-data.data';
import { UtilsService } from '../shared/services/utils/utils.service';
import { Settings } from '../settings-data/settings.model';
import { Document } from '../document-data/document.model';
import { Toggles } from '../features-data/sub-models/toggles.model';
import { has, isNumber, keys, reduce } from 'lodash';
import { AccountQuotas } from '../features-data/sub-models/account-quotas.model';
import { EmailService } from '../shared/services/email/email.service';
import { TranslateService } from '@ngx-translate/core';
import { MoneyPipe } from '../shared/pipes/money/money.pipe';
import { FriendlyDatePipe } from '../shared/pipes/friendly-date/friendly-date.pipe';
import { DateTimeService, MOMENT_TIMEZONELESS_DATE_FORMAT } from 'i2g-ng-utils';

@Injectable()
export class InvoiceDataBuilder extends DocumentDataBuilder<Invoice> {
  model = Invoice;
  entityType: EntityTypesEnum = EntityTypesEnum.invoice;
  documentType: DocumentTypesEnum = DocumentTypesEnum.invoice;
  quotaName = AccountQuotas.KEYS.INVOICES;
  featureName: string = Toggles.KEYS.INVOICES;
  depositFeatureName: string = Toggles.KEYS.DEPOSITS_ON_INVOICE;
  listFilterKeys: IFetchFilterKey = {
    [InvoiceFiltersEnum.paid]: PAID_LIST_SEARCH_PARAMS,
    [InvoiceFiltersEnum.unpaid]: UNPAID_LIST_SEARCH_PARAMS,
  };

  constructor(
    dateTimeService: DateTimeService,
    calculatorService: CalculatorService,
    utilsService: UtilsService,
    emailService: EmailService,
    translateService: TranslateService,
    moneyPipe: MoneyPipe,
    friendlyDatePipe: FriendlyDatePipe,
    urlService: UrlService
  ) {
    super(
      dateTimeService,
      calculatorService,
      utilsService,
      emailService,
      translateService,
      moneyPipe,
      friendlyDatePipe,
      urlService
    );
  }

  buildSortItemsComparatorFn(
    sortBy: SortByEnum,
    sortOrder: SortOrderEnum
  ): (a: Invoice, b: Invoice) => number {
    const order = sortOrder === SortOrderEnum.asc ? 1 : -1;

    return (a: Invoice, b: Invoice): number => {
      let sortValue = 0;

      switch (sortBy) {
        case SortByEnum.doc_number:
          sortValue = this.docNumberComparatorFn(a, b);
          break;
        case SortByEnum.billing_name:
          sortValue =
            this.billingNameComparatorFn(a, b) || this.emailComparatorFn(a, b);
          break;
        case SortByEnum.doc_date:
          sortValue =
            this.docDateComparatorFn(a, b) ||
            this.billingNameComparatorFn(a, b) ||
            this.emailComparatorFn(a, b);
          break;
        case SortByEnum.due_date:
          sortValue =
            this._dueDateComparator(a, b) ||
            this.billingNameComparatorFn(a, b) ||
            this.emailComparatorFn(a, b);
          break;
        case SortByEnum.date_paid:
          sortValue =
            this._datePaidComparator(a, b) ||
            this.billingNameComparatorFn(a, b) ||
            this.emailComparatorFn(a, b);
          break;
      }

      return sortValue * order;
    };
  }

  buildListItemsFilterFn(
    searchParams: ISearchParams,
    listFilter: string,
    settings: Settings,
    toggles: Toggles
  ): (invoice: Invoice) => boolean {
    const year = +searchParams[this.yearFilterKey];
    const taxYearStart = settings.companySettings.taxYearStart;
    const paidUnpaidFilter =
      listFilter === InvoiceFiltersEnum.paid
        ? (invoice: Invoice) => this._isPaidInvoice(invoice, toggles)
        : (invoice: Invoice) => !this._isPaidInvoice(invoice, toggles);
    const yearFilter =
      year && year !== ALL_YEARS
        ? (invoice: Invoice) =>
            this.utilsService.isInTaxYear(taxYearStart, year, invoice.docDate)
        : () => true;

    return listFilter !== SEARCH_LIST_FILTER
      ? (invoice: Invoice) => paidUnpaidFilter(invoice) && yearFilter(invoice)
      : (invoice: Invoice) => yearFilter(invoice);
  }

  buildListFilter(
    invoice: Invoice,
    settings: Settings,
    toggles: Toggles
  ): string {
    return this._isPaidInvoice(invoice, toggles)
      ? InvoiceFiltersEnum.paid
      : InvoiceFiltersEnum.unpaid;
  }

  validateSearchParams(searchParams: ISearchParams, filter: string): boolean {
    const validSortBy = [
      SortByEnum.doc_number,
      SortByEnum.billing_name,
      SortByEnum.doc_date,
      SortByEnum.due_date,
      SortByEnum.date_paid,
    ];
    const validParams = {
      [SearchParamsEnum.sort_by]: (a) => validSortBy.includes(a),
      [SearchParamsEnum.sort_order]: (a) =>
        [SortOrderEnum.asc, SortOrderEnum.desc].includes(a),
      [SearchParamsEnum.document_tax_year]: isNumber,
    };
    const requiredKeys = keys(validParams).every((key) =>
      has(searchParams, key)
    );
    const validValues = reduce(
      searchParams,
      (result, value, key) =>
        result && has(validParams, key) && validParams[key](value),
      true
    );

    return requiredKeys && validValues;
  }

  private _isPaidInvoice(invoice: Invoice, toggles: Toggles) {
    return (
      this.utilsService.calculateDocState(invoice.states, toggles) ===
      DocumentOverallStatesEnum.fully_paid
    );
  }

  /*** Invoice comparators ***/
  private _dueDateComparator(a: Document, b: Document): number {
    const getDueDate = (document: Document) =>
      this.dateTimeService.getMilliseconds(
        document.dueDate,
        MOMENT_TIMEZONELESS_DATE_FORMAT
      );

    return this.sortOrder(getDueDate(a), getDueDate(b));
  }

  private _datePaidComparator(a: Document, b: Document): number {
    const getDatePaid = (document: Document) =>
      this.dateTimeService.getMilliseconds(
        document.datePaid,
        MOMENT_TIMEZONELESS_DATE_FORMAT
      );

    return this.sortOrder(getDatePaid(a), getDatePaid(b));
  }
}
